import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, IconButton, Typography, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Grid, Chip, Tooltip, MenuItem, Divider, CircularProgress,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add, Search, Visibility, Warning, Upload, Delete, Edit,
  FolderZip, CheckCircle, InsertDriveFile, Article, FolderOpen } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import BulkBoeImport from './BulkBoeImport';
import StatusChip from '../common/StatusChip';
import { MobileCard, MobileRow } from '../common/MobileCard';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY_ITEM = { sku: '', description: '', hs_code: '', quantity: '', unit: 'PCS', unit_value: '', total_value: '' };

// Fuzzy-match a name string against a list of beneficiaries, return best id
function findBestBeneficiaryId(name, beneficiaries) {
  if (!name || !beneficiaries.length) return '';
  const normalize = s => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
  const norm = normalize(name);
  const words = norm.split(/\s+/).filter(w => w.length > 2);
  if (!words.length) return '';

  let bestId = '', bestScore = 0;
  for (const b of beneficiaries) {
    const bNorm = normalize(b.name);
    const bWords = bNorm.split(/\s+/).filter(w => w.length > 2);

    // Score 1: word overlap (each word from PDF checked against beneficiary words)
    const overlap = words.filter(w => bWords.some(bw => bw.includes(w) || w.includes(bw))).length;
    const overlapScore = overlap / Math.max(words.length, bWords.length, 1);

    // Score 2: does the full beneficiary name appear anywhere inside the PDF name or vice versa?
    const containsScore = (bNorm.includes(norm) || norm.includes(bNorm)) ? 0.8 : 0;

    const score = Math.max(overlapScore, containsScore);
    if (score > bestScore) { bestScore = score; bestId = String(b.id); }
  }
  // Accept if at least 1 meaningful word matched
  return bestScore >= 0.2 ? bestId : '';
}

const DEFAULT_FORM = {
  boe_number: '', hawb_number: '', boe_date: new Date().toISOString().split('T')[0],
  port_of_entry: 'INDEL4',
  beneficiary_id: '', currency: 'USD',
  invoice_value: '', assessable_value: '',
  bcd_amount: '', aidc_amount: '', sw_surcharge: '', igst_amount: '',
  duty_amount: '', total_value: '', notes: ''
};

export default function BOEList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canEdit = isAdmin || isManager;
  const canDelete = isAdmin;

  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialog, setDialog] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [boeItems, setBoeItems] = useState([{ ...EMPTY_ITEM }]);
  const [files, setFiles] = useState({ boe_file: null, invoice_file: null, awb_file: null, fedex_invoice_file: null });
  const [zipParsed, setZipParsed] = useState(null);   // result from parse-zip API
  const [zipConsignee, setZipConsignee] = useState(''); // raw name from BOE when no list match
  const [zipLoading, setZipLoading] = useState(false);
  const [zipDrag, setZipDrag] = useState(false);
  const zipInputRef = useRef();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get('/boe').then(r => setList(r.data.data));
  useEffect(() => {
    load();
    api.get('/beneficiaries').then(r => setBeneficiaries(r.data.data));
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(list.filter(b => {
      const matchSearch = !q ||
        b.boe_number?.toLowerCase().includes(q) ||
        b.beneficiary_name?.toLowerCase().includes(q) ||
        String(b.invoice_value || '').includes(q);
      const matchStatus = statusFilter === 'all' || b.status === statusFilter;
      return matchSearch && matchStatus;
    }));
  }, [search, statusFilter, list]);

  // Auto-calculate duty_amount = BCD + AIDC + SW Surcharge (without IGST)
  const calcDuty = (f) => {
    const bcd = parseFloat(f.bcd_amount) || 0;
    const aidc = parseFloat(f.aidc_amount) || 0;
    const sw = parseFloat(f.sw_surcharge) || 0;
    return (bcd + aidc + sw).toFixed(2);
  };

  const handleDutyChange = (field, val) => {
    const updated = { ...form, [field]: val };
    updated.duty_amount = calcDuty(updated);
    setForm(updated);
  };

  const updateBoeItem = (idx, field, val) => {
    const updated = [...boeItems];
    updated[idx] = { ...updated[idx], [field]: val };
    if (field === 'quantity' || field === 'unit_value') {
      const qty = parseFloat(field === 'quantity' ? val : updated[idx].quantity) || 0;
      const uv = parseFloat(field === 'unit_value' ? val : updated[idx].unit_value) || 0;
      updated[idx].total_value = (qty * uv).toFixed(2);
    }
    setBoeItems(updated);
  };

  // ZIP drag/drop + parse
  const handleZipFile = useCallback(async (file) => {
    if (!file || !file.name.match(/\.zip$/i)) { setError('Please upload a .zip file'); return; }
    setZipLoading(true); setZipParsed(null); setError('');
    try {
      const fd = new FormData();
      fd.append('zip_file', file);
      const res = await api.post('/boe/parse-zip', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.data;
      setZipParsed(d);

      // Fuzzy-match beneficiary from consignee name in PDF
      const matchedBeneficiaryId = findBestBeneficiaryId(d.consignee, beneficiaries);
      // If no match, store the raw name so we can show it in the UI
      setZipConsignee(d.consignee && !matchedBeneficiaryId ? d.consignee : '');

      // Auto-populate form from all parsed data
      setForm(prev => ({
        ...prev,
        boe_number:       d.boe_number       || prev.boe_number,
        hawb_number:      d.hawb             || prev.hawb_number,
        boe_date:         d.boe_date         || prev.boe_date,
        port_of_entry:    d.port_of_entry    || prev.port_of_entry,
        currency:         'USD', // BOE invoice values are always in USD
        beneficiary_id:   matchedBeneficiaryId || prev.beneficiary_id,
        invoice_value:    d.invoice_value    != null ? String(d.invoice_value)    : prev.invoice_value,
        assessable_value: d.assessable_value != null ? String(d.assessable_value) : prev.assessable_value,
        total_value:      d.total_value      != null ? String(d.total_value)      : prev.total_value,
        bcd_amount:       d.bcd_amount       != null ? String(d.bcd_amount)       : prev.bcd_amount,
        aidc_amount:      d.aidc_amount      != null ? String(d.aidc_amount)      : prev.aidc_amount,
        sw_surcharge:     d.sw_surcharge     != null ? String(d.sw_surcharge)     : prev.sw_surcharge,
        igst_amount:      d.igst_amount      != null ? String(d.igst_amount)      : prev.igst_amount,
        // Recalculate duty total from extracted fields
        duty_amount: (() => {
          const bcd  = d.bcd_amount    || 0;
          const aidc = d.aidc_amount   || 0;
          const sw   = d.sw_surcharge  || 0;
          return (parseFloat(bcd) + parseFloat(aidc) + parseFloat(sw)).toFixed(2);
        })(),
      }));

      // Populate line items if extracted from CI
      if (d.items && d.items.length > 0) {
        setBoeItems(d.items);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to parse ZIP');
    } finally { setZipLoading(false); }
  }, [beneficiaries]);

  const handleZipDrop = useCallback((e) => {
    e.preventDefault(); setZipDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleZipFile(file);
  }, [handleZipFile]);

  const openAdd = () => {
    setEditId(null);
    setForm(DEFAULT_FORM);
    setBoeItems([{ ...EMPTY_ITEM }]);
    setFiles({ boe_file: null, invoice_file: null, awb_file: null, fedex_invoice_file: null });
    setZipParsed(null); setZipConsignee('');
    setError('');
    setDialog(true);
  };

  const openEdit = async (b) => {
    setEditId(b.id);
    setForm({
      boe_number: b.boe_number || '',
      hawb_number: b.hawb_number || '',
      boe_date: b.boe_date || new Date().toISOString().split('T')[0],
      port_of_entry: b.port_of_entry || 'INDEL4',
      beneficiary_id: b.beneficiary_id || '',
      currency: b.currency || 'USD',
      invoice_value: b.invoice_value || '',
      assessable_value: b.assessable_value || '',
      bcd_amount: b.bcd_amount || '',
      aidc_amount: b.aidc_amount || '',
      sw_surcharge: b.sw_surcharge || '',
      igst_amount: b.igst_amount || '',
      duty_amount: b.duty_amount || '',
      total_value: b.total_value || '',
      notes: b.notes || ''
    });
    // Load items
    try {
      const res = await api.get(`/boe/${b.id}`);
      const items = res.data.data.items || [];
      setBoeItems(items.length ? items.map(i => ({
        sku: i.sku || '', description: i.description || '', hs_code: i.hs_code || '',
        quantity: i.quantity || '', unit: i.unit || 'PCS',
        unit_value: i.unit_value || '', total_value: i.total_value || ''
      })) : [{ ...EMPTY_ITEM }]);
    } catch { setBoeItems([{ ...EMPTY_ITEM }]); }
    setFiles({ boe_file: null, invoice_file: null, awb_file: null, fedex_invoice_file: null });
    setZipParsed(null);
    setError('');
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.boe_number || !form.boe_date) { setError('BOE number and date required'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v !== '' && v !== null && fd.append(k, v));
      // Append items
      const validItems = boeItems.filter(i => i.description || i.sku);
      if (validItems.length) fd.append('items', JSON.stringify(validItems));
      // Newly selected files (override ZIP)
      if (files.boe_file)           fd.append('boe_file',           files.boe_file);
      if (files.invoice_file)       fd.append('invoice_file',       files.invoice_file);
      if (files.awb_file)           fd.append('awb_file',           files.awb_file);
      if (files.fedex_invoice_file) fd.append('fedex_invoice_file', files.fedex_invoice_file);
      // Paths pre-saved from ZIP parse (only if no new file uploaded)
      if (zipParsed?.boe_file_path        && !files.boe_file)           fd.append('zip_boe_file_path',        zipParsed.boe_file_path);
      if (zipParsed?.invoice_file_path    && !files.invoice_file)       fd.append('zip_invoice_file_path',    zipParsed.invoice_file_path);
      if (zipParsed?.awb_file_path        && !files.awb_file)           fd.append('zip_awb_file_path',        zipParsed.awb_file_path);
      if (zipParsed?.fedex_invoice_path   && !files.fedex_invoice_file) fd.append('zip_fedex_invoice_path',   zipParsed.fedex_invoice_path);

      if (editId) {
        await api.put(`/boe/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/boe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setDialog(false);
      setFiles({ boe_file: null, invoice_file: null, awb_file: null });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving BOE');
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/boe/${deleteDialog.id}`);
      setDeleteDialog(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting BOE');
      setDeleteDialog(null);
    }
  };

  const pendingCount = list.filter(b => b.status === 'pending').length;

  return (
    <Box>
      <PageHeader title="Bill of Entry (BOE)" subtitle="Manage BOE records and knock-off"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<FolderOpen />} size={isMobile ? 'small' : 'medium'}
              onClick={() => setBulkOpen(true)}>
              {isMobile ? 'Bulk' : 'Bulk Import'}
            </Button>
            <Button variant="contained" startIcon={<Add />} size={isMobile ? 'small' : 'medium'} onClick={openAdd}>
              {isMobile ? 'Add BOE' : 'Add BOE Entry'}
            </Button>
          </Box>
        }
      />

      {pendingCount > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <strong>{pendingCount} BOE(s)</strong> pending knock-off on Kotak portal
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <TextField size="small" fullWidth placeholder="Search BOE number, beneficiary, invoice value…" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160 }}>
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="partial">Partial</MenuItem>
          <MenuItem value="knocked_off">Knocked Off</MenuItem>
        </TextField>
      </Box>

      {/* Mobile cards */}
      {isMobile ? (
        <Box>
          {filtered.length === 0
            ? <Card><Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No BOE entries</Box></Card>
            : filtered.map(b => (
              <MobileCard key={b.id} onClick={() => navigate(`/boe/${b.id}`)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: 11, color: 'primary.main' }}>
                      {b.boe_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{b.boe_date}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <StatusChip status={b.status} />
                    {canEdit && (
                      <IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(b); }}><Edit fontSize="small" /></IconButton>
                    )}
                    {canDelete && (
                      <IconButton size="small" onClick={e => { e.stopPropagation(); setDeleteDialog(b); }} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton>
                    )}
                  </Box>
                </Box>
                {b.beneficiary_name && <MobileRow label="Supplier" value={b.beneficiary_name} />}
                <MobileRow label="Invoice Value" value={`${b.currency} ${parseFloat(b.invoice_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} bold />
                {b.assessable_value && <MobileRow label="Assessable" value={`₹${parseFloat(b.assessable_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />}
                {b.duty_amount && <MobileRow label="Duty (BCD+AIDC+SW)" value={`₹${parseFloat(b.duty_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                  {b.boe_file_path     && <Chip label="BOE" size="small" color="primary" variant="outlined" />}
                  {b.invoice_file_path && <Chip label="INV" size="small" color="info"    variant="outlined" />}
                  {b.awb_file_path     && <Chip label="AWB" size="small" color="secondary" variant="outlined" />}
                </Box>
              </MobileCard>
            ))
          }
        </Box>
      ) : (
        /* Desktop table */
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>BOE / CBE-XIII Number</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Beneficiary</TableCell>
                <TableCell align="right">Invoice Value</TableCell>
                <TableCell align="right">Assessable (INR)</TableCell>
                <TableCell>Duty Breakdown (INR)</TableCell>
                <TableCell>Docs</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No BOE entries</TableCell></TableRow>
              ) : filtered.map(b => (
                <TableRow key={b.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'primary.main' }}>{b.boe_number}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{b.boe_date}</TableCell>
                  <TableCell><Typography variant="body2">{b.beneficiary_name || '—'}</Typography></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{b.currency} {parseFloat(b.invoice_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>₹{parseFloat(b.assessable_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    {b.bcd_amount ? <div>BCD: ₹{parseFloat(b.bcd_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div> : null}
                    {b.aidc_amount ? <div>AIDC: ₹{parseFloat(b.aidc_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div> : null}
                    {b.sw_surcharge ? <div>SW: ₹{parseFloat(b.sw_surcharge).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div> : null}
                    {b.igst_amount ? <div style={{ color: '#888' }}>IGST: ₹{parseFloat(b.igst_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div> : null}
                    {b.duty_amount ? <div style={{ fontWeight: 700 }}>Total: ₹{parseFloat(b.duty_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div> : '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {b.boe_file_path     && <Chip label="BOE" size="small" color="primary"   variant="outlined" />}
                      {b.invoice_file_path && <Chip label="INV" size="small" color="info"       variant="outlined" />}
                      {b.awb_file_path     && <Chip label="AWB" size="small" color="secondary"  variant="outlined" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={b.status} />
                    {b.knockoff_count > 0 && <Typography variant="caption" display="block" color="text.secondary">{b.knockoff_count} knockoff(s)</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View & Knock-off">
                      <IconButton size="small" onClick={() => navigate(`/boe/${b.id}`)} sx={{ color: 'primary.main' }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(b)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteDialog(b)} sx={{ color: 'error.main' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit BOE Dialog */}
      <Dialog open={dialog} onClose={(_, reason) => { if (reason !== 'backdropClick') setDialog(false); }} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editId ? 'Edit Bill of Entry' : 'Add Bill of Entry'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* ── ZIP Drop Zone ── */}
          <input ref={zipInputRef} type="file" accept=".zip" hidden
            onChange={e => { handleZipFile(e.target.files[0]); e.target.value = ''; }} />
          <Box
            onDragOver={e => { e.preventDefault(); setZipDrag(true); }}
            onDragLeave={() => setZipDrag(false)}
            onDrop={handleZipDrop}
            onClick={() => zipInputRef.current?.click()}
            sx={{
              mb: 2, mt: 1, p: 2.5, border: '2px dashed',
              borderColor: zipDrag ? 'primary.main' : zipParsed ? 'success.main' : 'divider',
              borderRadius: 2, textAlign: 'center', cursor: 'pointer',
              bgcolor: zipDrag ? 'primary.50' : zipParsed ? 'success.50' : 'background.default',
              transition: 'all .2s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
            }}>
            {zipLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress size={18} /><Typography variant="body2">Parsing ZIP…</Typography>
              </Box>
            ) : zipParsed ? (
              <Box>
                <CheckCircle sx={{ color: 'success.main', mb: 0.5 }} />
                <Typography variant="body2" color="success.main" fontWeight={600}>ZIP parsed successfully</Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 1 }}>
                  {zipParsed.files.map(f => (
                    <Chip key={f.saved_name} size="small"
                      icon={<InsertDriveFile />}
                      label={`${f.original_name.length > 25 ? f.original_name.substring(0,25)+'…' : f.original_name} → ${
                        f.role === 'boe' ? 'BOE' : f.role === 'invoice' ? 'Invoice' :
                        f.role === 'awb' ? 'AWB' : f.role === 'fedex_invoice' ? 'FedEx Invoice' : 'Unknown'
                      }`}
                      color={f.role === 'unknown' ? 'default' : 'success'}
                      variant={f.role === 'unknown' ? 'outlined' : 'filled'}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Click to upload a different ZIP
                </Typography>
              </Box>
            ) : (
              <Box>
                <FolderZip sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="body2" fontWeight={600}>Drop EDMDocument ZIP here</Typography>
                <Typography variant="caption" color="text.secondary">
                  or click to browse — auto-fills BOE number, date, and uploads all documents
                </Typography>
              </Box>
            )}
          </Box>

          {zipParsed && (zipParsed.mawb || zipParsed.consignee) && (
            <Alert severity="info" sx={{ mb: 1.5 }} icon={<Article />}>
              {/* parser's `consignee` field carries the shipper (Name of Consignor) used for supplier matching */}
              {zipParsed.consignee && <><strong>Supplier:</strong> {zipParsed.consignee} &nbsp;·&nbsp; </>}
              {zipParsed.mawb && <><strong>MAWB:</strong> {zipParsed.mawb}</>}
              {zipParsed.hawb && <> &nbsp;·&nbsp; <strong>HAWB:</strong> {zipParsed.hawb}</>}
              {zipParsed.fedex_invoice_number && <> &nbsp;·&nbsp; <strong>FedEx Inv:</strong> {zipParsed.fedex_invoice_number}</>}
              {zipParsed.items?.length > 0 && <> &nbsp;·&nbsp; <strong>{zipParsed.items.length} line item(s) extracted</strong></>}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid item xs={12} sm={8}>
              <TextField label="BOE / CBE-XIII Number *" fullWidth value={form.boe_number}
                onChange={e => setForm({ ...form, boe_number: e.target.value })}
                placeholder="e.g. CBEXIII_DEL_2025-2026_06_02_18391" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="HAWB Number" fullWidth value={form.hawb_number}
                onChange={e => setForm({ ...form, hawb_number: e.target.value })}
                placeholder="House Airway Bill No." />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="BOE Date *" type="date" fullWidth value={form.boe_date}
                onChange={e => setForm({ ...form, boe_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Currency" select fullWidth value={form.currency}
                onChange={e => setForm({ ...form, currency: e.target.value })}>
                {['USD','EUR','CNY'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Port of Entry" fullWidth value={form.port_of_entry}
                onChange={e => setForm({ ...form, port_of_entry: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Supplier" select fullWidth value={form.beneficiary_id}
                onChange={e => { setForm({ ...form, beneficiary_id: e.target.value }); setZipConsignee(''); }}
                helperText={zipConsignee ? `From BOE: "${zipConsignee}" — not in your list` : ''}
                FormHelperTextProps={{ sx: { color: 'warning.main', fontStyle: 'italic' } }}>
                <MenuItem value="">-- Select --</MenuItem>
                {beneficiaries.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Invoice Value (USD)" type="number" fullWidth value={form.invoice_value}
                onChange={e => setForm({ ...form, invoice_value: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Assessable Value (INR)" type="number" fullWidth value={form.assessable_value}
                onChange={e => setForm({ ...form, assessable_value: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Total Value (INR)" type="number" fullWidth value={form.total_value}
                onChange={e => setForm({ ...form, total_value: e.target.value })} />
            </Grid>

            {/* Duty Breakdown */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Duty Breakdown (INR)</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="BCD Amount" type="number" fullWidth value={form.bcd_amount}
                onChange={e => handleDutyChange('bcd_amount', e.target.value)}
                placeholder="Basic Customs Duty" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="AIDC Amount" type="number" fullWidth value={form.aidc_amount}
                onChange={e => handleDutyChange('aidc_amount', e.target.value)}
                placeholder="Agri Infra Dev Cess" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="SW Surcharge" type="number" fullWidth value={form.sw_surcharge}
                onChange={e => handleDutyChange('sw_surcharge', e.target.value)}
                placeholder="Social Welfare Surcharge" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="IGST Amount" type="number" fullWidth value={form.igst_amount}
                onChange={e => setForm({ ...form, igst_amount: e.target.value })}
                placeholder="IGST (ITC reclaimable)" helperText="Not included in total" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, bgcolor: 'primary.50', bgcolor: '#e8eaf6', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Duty Total (BCD + AIDC + SW)</Typography>
                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                  ₹ {parseFloat(form.duty_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField label="Notes" fullWidth multiline rows={2} value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Grid>

            {/* File uploads */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Documents</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}
                color={(files.boe_file || zipParsed?.boe_file_path) ? 'success' : 'primary'} size="small">
                {files.boe_file ? '✓ BOE' : zipParsed?.boe_file_path ? '✓ BOE (ZIP)' : 'Upload BOE'}
                <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setFiles({ ...files, boe_file: e.target.files[0] })} />
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}
                color={(files.invoice_file || zipParsed?.invoice_file_path) ? 'success' : 'primary'} size="small">
                {files.invoice_file ? '✓ Invoice' : zipParsed?.invoice_file_path ? '✓ Invoice (ZIP)' : 'Upload Invoice'}
                <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                  onChange={e => setFiles({ ...files, invoice_file: e.target.files[0] })} />
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}
                color={(files.awb_file || zipParsed?.awb_file_path) ? 'success' : 'primary'} size="small">
                {files.awb_file ? '✓ AWB' : zipParsed?.awb_file_path ? '✓ AWB (ZIP)' : 'Upload AWB'}
                <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setFiles({ ...files, awb_file: e.target.files[0] })} />
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}
                color={(files.fedex_invoice_file || zipParsed?.fedex_invoice_path) ? 'success' : 'primary'} size="small">
                {files.fedex_invoice_file ? '✓ FedEx Inv' : zipParsed?.fedex_invoice_path ? '✓ FedEx Inv (ZIP)' : 'FedEx Tax Inv'}
                <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setFiles({ ...files, fedex_invoice_file: e.target.files[0] })} />
              </Button>
            </Grid>

            {/* BOE Line Items */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2">Line Items (SKU / Products)</Typography>
                <Button size="small" startIcon={<Add />} variant="outlined"
                  onClick={() => setBoeItems([...boeItems, { ...EMPTY_ITEM }])}>
                  Add Item
                </Button>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 100 }}>SKU</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Description</TableCell>
                      <TableCell sx={{ minWidth: 90 }}>HS Code</TableCell>
                      <TableCell sx={{ minWidth: 70 }}>Qty</TableCell>
                      <TableCell sx={{ minWidth: 70 }}>Unit</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Unit Value</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Total Value</TableCell>
                      <TableCell sx={{ width: 40 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {boeItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField size="small" fullWidth value={item.sku}
                            onChange={e => updateBoeItem(idx, 'sku', e.target.value)} placeholder="e.g. TAG-55x18" />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth value={item.description}
                            onChange={e => updateBoeItem(idx, 'description', e.target.value)} placeholder="Proximity RFID Tag" />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth value={item.hs_code}
                            onChange={e => updateBoeItem(idx, 'hs_code', e.target.value)} placeholder="85235910" />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" fullWidth value={item.quantity}
                            onChange={e => updateBoeItem(idx, 'quantity', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth value={item.unit}
                            onChange={e => updateBoeItem(idx, 'unit', e.target.value)}
                            placeholder="PCS" inputProps={{ style: { textTransform: 'uppercase' } }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" fullWidth value={item.unit_value}
                            onChange={e => updateBoeItem(idx, 'unit_value', e.target.value)} inputProps={{ step: '0.0001' }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" fullWidth value={item.total_value}
                            onChange={e => updateBoeItem(idx, 'total_value', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setBoeItems(boeItems.filter((_, i) => i !== idx))}
                            disabled={boeItems.length === 1}>
                            <Delete fontSize="small" sx={{ color: boeItems.length > 1 ? 'error.main' : 'text.disabled' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {editId ? 'Update BOE' : 'Save BOE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={Boolean(deleteDialog)} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete BOE Entry</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteDialog?.boe_number}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <BulkBoeImport
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        beneficiaries={beneficiaries}
        onDone={load}
      />
    </Box>
  );
}
